import axios from 'axios';
import AppConfig from './app-config';
import authHeader from './auth-header';
import AuthService from './auth';

const apiUrl = process.env.REACT_APP_MGO_API_URL;
const userCors = process.env.REACT_APP_USE_CORS;

// Configure axios defaults
if (userCors === 'true') {
  axios.defaults.withCredentials = true;
}

/**
 * Error types for categorization and analytics
 */
export const ErrorType = {
  NETWORK: 'network_error',
  UNAUTHORIZED: 'unauthorized',
  VALIDATION: 'validation_error',
  SERVER: 'server_error',
  TIMEOUT: 'timeout_error',
  UNKNOWN: 'unknown_error',
};

/**
 * Categorize error based on response
 */
function categorizeError(error) {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return ErrorType.TIMEOUT;
    }
    return ErrorType.NETWORK;
  }

  const status = error.response.status;
  if (status === 401) return ErrorType.UNAUTHORIZED;
  if (status === 422) return ErrorType.VALIDATION;
  if (status >= 500) return ErrorType.SERVER;
  return ErrorType.UNKNOWN;
}

/**
 * Extract error message from API response
 */
function extractErrorMessage(error, defaultMessage = 'Ha ocurrido un error, intenta de nuevo más tarde.') {
  if (!error.response) {
    return 'Error de conexión. Verifica tu conexión a internet.';
  }

  const { data } = error.response;
  
  // Try different message locations used in the API
  if (data?.data?.mensaje) return data.data.mensaje;
  if (data?.msg) return data.msg;
  if (data?.message) return data.message;
  
  return defaultMessage;
}

/**
 * Report error to analytics endpoint
 */
async function reportErrorAnalytics(endpoint, method, error, errorType, context = {}) {
  try {
    if (AuthService.shouldLimitAutomatedAgentRequests()) {
      return;
    }
    const payload = {
      evento: 'api_error',
      data: {
        endpoint,
        method,
        errorType,
        statusCode: error.response?.status || null,
        message: extractErrorMessage(error),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...context,
      },
    };

    // Fire and forget - don't await to avoid blocking
    axios.post(`${apiUrl}/event/report`, payload).catch(() => {
      // Silently fail analytics reporting
      console.debug('Failed to report error analytics');
    });
  } catch (e) {
    // Silently fail analytics reporting
    console.debug('Failed to report error analytics', e);
  }
}

/**
 * Standardized API error class
 */
export class ApiError extends Error {
  constructor(error, endpoint, method) {
    const message = extractErrorMessage(error);
    super(message);
    
    this.name = 'ApiError';
    this.originalError = error;
    this.endpoint = endpoint;
    this.method = method;
    this.type = categorizeError(error);
    this.statusCode = error.response?.status || null;
    this.data = error.response?.data || null;
    this.isUnauthorized = this.type === ErrorType.UNAUTHORIZED;
    this.isValidationError = this.type === ErrorType.VALIDATION;
    this.isNetworkError = this.type === ErrorType.NETWORK;
  }
}

/**
 * API Client configuration options
 */
const defaultOptions = {
  reportAnalytics: true,
  parseAppConfig: true,
  throwOnError: true,
};

/**
 * Create the API client instance
 */
class ApiClient {
  constructor() {
    this.baseUrl = apiUrl;
  }

  buildAxiosConfig(config = {}) {
    const headers = {
      ...(config.headers || {}),
      ...authHeader(),
    };
    return {
      ...config,
      headers,
    };
  }

  /**
   * Build full URL from endpoint
   */
  buildUrl(endpoint) {
    // If endpoint already starts with http, use as-is
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    // Remove leading slash if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseUrl}/${cleanEndpoint}`;
  }

  /**
   * Handle successful response
   */
  handleSuccess(response, options) {
    if (options.parseAppConfig) {
      AppConfig.parseResponse(response);
    }
    return response;
  }

  /**
   * Handle error response
   */
  async handleError(error, endpoint, method, options, context) {
    const apiError = new ApiError(error, endpoint, method);

    if (options.reportAnalytics) {
      await reportErrorAnalytics(endpoint, method, error, apiError.type, context);
    }

    if (options.throwOnError) {
      throw apiError;
    }

    return { error: apiError, data: null };
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}, context = {}) {
    const opts = { ...defaultOptions, ...options };
    const url = this.buildUrl(endpoint);
    const axiosConfig = this.buildAxiosConfig(options.axiosConfig);

    try {
      const response = await axios.get(url, axiosConfig);
      return this.handleSuccess(response, opts);
    } catch (error) {
      return this.handleError(error, endpoint, 'GET', opts, context);
    }
  }

  /**
   * POST request
   */
  async post(endpoint, data = {}, options = {}, context = {}) {
    const opts = { ...defaultOptions, ...options };
    const url = this.buildUrl(endpoint);
    const axiosConfig = this.buildAxiosConfig(options.axiosConfig);

    try {
      const response = await axios.post(url, data, axiosConfig);
      return this.handleSuccess(response, opts);
    } catch (error) {
      return this.handleError(error, endpoint, 'POST', opts, context);
    }
  }

  /**
   * PUT request
   */
  async put(endpoint, data = {}, options = {}, context = {}) {
    const opts = { ...defaultOptions, ...options };
    const url = this.buildUrl(endpoint);
    const axiosConfig = this.buildAxiosConfig(options.axiosConfig);

    try {
      const response = await axios.put(url, data, axiosConfig);
      return this.handleSuccess(response, opts);
    } catch (error) {
      return this.handleError(error, endpoint, 'PUT', opts, context);
    }
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}, context = {}) {
    const opts = { ...defaultOptions, ...options };
    const url = this.buildUrl(endpoint);
    const axiosConfig = this.buildAxiosConfig(options.axiosConfig);

    try {
      const response = await axios.delete(url, axiosConfig);
      return this.handleSuccess(response, opts);
    } catch (error) {
      return this.handleError(error, endpoint, 'DELETE', opts, context);
    }
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;

/**
 * Helper hook-style functions for common patterns
 */

/**
 * Wrapper that returns { data, error } instead of throwing
 * Useful for components that want to handle errors inline
 */
export async function safeApiCall(apiCallFn) {
  try {
    const response = await apiCallFn();
    return { data: response.data, error: null, response };
  } catch (error) {
    if (error instanceof ApiError) {
      return { data: null, error, response: null };
    }
    // Wrap unexpected errors
    const apiError = new ApiError(error, 'unknown', 'unknown');
    return { data: null, error: apiError, response: null };
  }
}

/**
 * Check if error requires redirect to login
 */
export function shouldRedirectToLogin(error) {
  return error instanceof ApiError && error.isUnauthorized;
}

/**
 * Get user-friendly error message for snackbar display
 */
export function getErrorMessage(error, defaultMessage = 'Ha ocurrido un error, intenta de nuevo más tarde.') {
  if (error instanceof ApiError) {
    return error.message;
  }
  return defaultMessage;
}
